import React from 'react'
import styled, { css } from 'styled-components'

import {
  Button,
  SFSymbolLabel,
  Tooltip,
  Truncate,
  useResponsiveDropdown,
} from '@sketch/components'

import { useHandleCopyValue, CopiableArea } from '@sketch/modules-common'
import { ReactComponent as ChevronUpDown } from '@sketch/icons/chevrons-up-down-16'

import { formatSymbolPath } from '../../../../../utils'
import { SymbolPathLinkDropdown } from '../../../SymbolPathLinkDropdown'

const Name = styled(Truncate)<{ $isSymbol?: boolean }>`
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  font-size: 0.8125rem; /* 13px */
  font-weight: ${({ theme }) => theme.fontWeights.medium};

  ${({ theme, $isSymbol }) =>
    $isSymbol &&
    css`
      color: ${theme.colors.inspect.symbol.A};
    `}
`

const SubName = styled(Truncate)`
  color: ${({ theme }) => theme.colors.foreground.secondary.C};
  font-size: 0.75rem; /* 12px */
`

const StyledChevronUpDown = styled(ChevronUpDown)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`

const DropdownButton = styled(Button)`
  display: flex;
  align-items: center;

  padding: 2px 4px; /* stylelint-disable-line scales/space */
  margin-left: -4px;

  font-weight: 400;

  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;

  ${StyledChevronUpDown} {
    opacity: 0;
  }

  :hover,
  &[aria-expanded='true'] {
    ${StyledChevronUpDown} {
      opacity: 1;
    }
  }

  &[aria-expanded='true'] {
    background-color: ${({ theme }) => theme.colors.border.A};
    color: ${({ theme }) => theme.colors.foreground.secondary.A};
  }
`

const DropdownButtonWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
`

interface ComponentNameProps extends React.PropsWithChildren<{}> {
  isSymbol?: boolean
}

export const ComponentName = ({ children, isSymbol }: ComponentNameProps) => {
  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(`${children}`)

  return (
    <CopiableArea
      onClick={handleCopyValue}
      onMouseEnter={handleEnterCopiableArea}
      onMouseLeave={handleLeaveCopiableArea}
    >
      <Tooltip
        placement="left"
        spacing="10px"
        visible={copyTooltipVisible}
        content={copyTooltipText}
      >
        <Name $isSymbol={isSymbol}>{children}</Name>
      </Tooltip>
    </CopiableArea>
  )
}

export const ComponentSubName: React.FC = ({ children }) => {
  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(`${children}`)

  return (
    <CopiableArea
      onClick={handleCopyValue}
      onMouseEnter={handleEnterCopiableArea}
      onMouseLeave={handleLeaveCopiableArea}
    >
      <Tooltip
        placement="left"
        spacing="10px"
        visible={copyTooltipVisible}
        content={copyTooltipText}
      >
        <SubName>{children}</SubName>
      </Tooltip>
    </CopiableArea>
  )
}

interface SymbolPathProps {
  paths: string[]
}

const DROPDOWN_POSITION = [-8, 8] as [number, number]

export const SymbolPath = ({ paths }: SymbolPathProps) => {
  const [content, buttonProps, { visible: dropdownVisible }] =
    useResponsiveDropdown({
      dropdown: SymbolPathLinkDropdown,
      dropdownProps: { paths },
      placement: 'bottom-start',
      offset: DROPDOWN_POSITION,
    })

  const pathsWithSpace = paths.map((path, index) => (
    <div key={path}>
      <SFSymbolLabel text={formatSymbolPath(path, index)} />
    </div>
  ))

  return (
    <Tooltip
      content={pathsWithSpace}
      contentStyle={{ whiteSpace: 'pre' }}
      placement="bottom"
      disabled={dropdownVisible}
    >
      <DropdownButton
        variant="ghost"
        size="24"
        contentWrapper={DropdownButtonWrapper}
        {...buttonProps}
      >
        <SubName>
          <SFSymbolLabel text={paths.join(' / ')} />
        </SubName>
        <StyledChevronUpDown />
      </DropdownButton>
      {content}
    </Tooltip>
  )
}
